// src/components/AdminPanel.js 
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Table, Container, Form, Button, Modal } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { isLoggedIn, getToken, getRole } from '../services/auth';
import './AdminPanel.css';

const API = 'https://api.sevicol.com.uy';

const AdminPanel = () => {
  const [rows, setRows] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [filtros, setFiltros] = useState({
    departamento: '',
    ciudad: '',
    cargo: '',
    fechaDesde: '',
    fechaHasta: ''
  });

  const [query, setQuery] = useState('');         // 🔎 búsqueda
  const [page, setPage] = useState(1);           // 📄 paginación
  const [pageSize, setPageSize] = useState(10);  // filas por página

  const [selected, setSelected] = useState(new Set());
  const [selectAllPage, setSelectAllPage] = useState(false);

  // ===== Modal de confirmación =====
  const [confirm, setConfirm] = useState({
    show: false,
    type: null,            // 'one' | 'selected' | 'all'
    payload: null,         // { id } | { ids } | { count }
    title: '',
    message: '',
    loading: false
  });

  // ===== Modal detalle (Estudios) =====
  const [detalle, setDetalle] = useState({
    show: false,
    titulo: '',
    texto: ''
  });

  const navigate = useNavigate();

  // 🔐 rol del usuario
  const role = getRole();
  const isAdmin = role === 'admin'; // true solo para admin

  // Auth guard
  useEffect(() => {
    if (!isLoggedIn()) navigate('/login', { replace: true });
  }, [navigate]);

  // Carga
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getToken();
        const { data } = await axios.get(`${API}/api/cv/listado`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        setRows(data || []);

        const deps = Array.from(new Set((data || []).map(r => r.departamento).filter(Boolean)))
          .sort((a, b) => a.localeCompare(b));
        setDepartamentos(deps);
      } catch (err) {
        console.error('Error listado:', err);
      }
    };
    fetchData();
  }, []);

  // Ciudades dependientes
  useEffect(() => {
    if (filtros.departamento) {
      const c = rows
        .filter(r => r.departamento === filtros.departamento)
        .map(r => r.ciudad)
        .filter(Boolean);
      setCiudades(Array.from(new Set(c)).sort((a, b) => a.localeCompare(b)));
    } else setCiudades([]);
  }, [filtros.departamento, rows]);

  // ====== FILTRO + BUSQUEDA ======
  const filtered = useMemo(() => {
    let res = [...rows];

    // Departamento
    if (filtros.departamento) {
      res = res.filter(r => r.departamento === filtros.departamento);
    }

    // Ciudad
    if (filtros.ciudad) {
      res = res.filter(r => r.ciudad === filtros.ciudad);
    }

    // Cargo
    if (filtros.cargo) {
      res = res.filter(r => r.cargo === filtros.cargo);
    }

    // Fecha de envío (r.fecha)
    if (filtros.fechaDesde) {
      const desde = new Date(filtros.fechaDesde);
      res = res.filter(r => new Date(r.fecha) >= desde);
    }
    if (filtros.fechaHasta) {
      const hasta = new Date(filtros.fechaHasta);
      hasta.setHours(23, 59, 59, 999);
      res = res.filter(r => new Date(r.fecha) <= hasta);
    }

    // Búsqueda general
    if (query.trim()) {
      const q = query.toLowerCase();
      res = res.filter(r => {
        const campos = [
          r.nombre, r.apellido, r.documento, r.email, r.telefono,
          r.departamento, r.ciudad, r.direccion,
          r.cargo,
          r.situacion_laboral, r.detalle_situacion,
          r.estudios,
          // aunque no se muestren, los dejo en la búsqueda:
          r.ref_laborales, r.ref_personales, r.motivacion
        ].map(v => (v || '').toString().toLowerCase());
        return campos.some(t => t.includes(q));
      });
    }

    return res;
  }, [rows, filtros, query]);

  // ====== PAGINACIÓN ======
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    setPage(1);
    setSelected(new Set());
    setSelectAllPage(false);
  }, [filtros, query, pageSize]);

  const pageSlice = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const goTo = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  // ====== SELECT (por página) ======
  const toggleRow = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const toggleSelectAllPage = () => {
    if (selectAllPage) {
      const next = new Set(selected);
      pageSlice.forEach(r => next.delete(r.id));
      setSelected(next);
      setSelectAllPage(false);
    } else {
      const next = new Set(selected);
      pageSlice.forEach(r => next.add(r.id));
      setSelected(next);
      setSelectAllPage(true);
    }
  };

  useEffect(() => {
    const allInPage = pageSlice.length > 0 && pageSlice.every(r => selected.has(r.id));
    setSelectAllPage(allInPage);
  }, [pageSlice, selected]);

  // ====== Acciones (sin confirm) ======
  const performDeleteOne = async (id) => {
    if (!isAdmin) return;
    await axios.delete(`${API}/api/cv/${id}`);
    const nextRows = rows.filter(r => r.id !== id);
    setRows(nextRows);
    const nextSel = new Set(selected); nextSel.delete(id); setSelected(nextSel);
  };

  const performDeleteSelected = async (ids) => {
    if (!isAdmin) return;
    await axios.delete(`${API}/api/cv`, { data: { ids } });
    setRows(prev => prev.filter(r => !ids.includes(r.id)));
    const nextSel = new Set(selected); ids.forEach(id => nextSel.delete(id)); setSelected(nextSel);
  };

  const performDeleteAll = async () => {
    if (!isAdmin) return;
    await axios.delete(`${API}/api/cv`, { data: { all: true } });
    setRows([]);
    setSelected(new Set());
  };

  // ====== Abrir modal según acción ======
  const askDeleteOne = (id) => {
    if (!isAdmin) return;
    const row = rows.find(r => r.id === id);
    setConfirm({
      show: true,
      type: 'one',
      payload: { id },
      title: 'Eliminar postulante',
      message: `¿Seguro que deseas eliminar a "${row?.nombre || ''} ${row?.apellido || ''}"? Esta acción no se puede deshacer.`,
      loading: false
    });
  };

  const askDeleteSelected = () => {
    if (!isAdmin) return;
    const ids = pageSlice.filter(r => selected.has(r.id)).map(r => r.id);
    if (!ids.length) return;
    setConfirm({
      show: true,
      type: 'selected',
      payload: { ids, count: ids.length },
      title: 'Eliminar seleccionados',
      message: `Se eliminarán ${ids.length} postulante(s) seleccionados en esta página. ¿Confirmás?`,
      loading: false
    });
  };

  const askDeleteAll = () => {
    if (!isAdmin) return;
    if (!filtered.length) return;
    setConfirm({
      show: true,
      type: 'all',
      payload: { count: filtered.length },
      title: 'Eliminar todos',
      message: '⚠️ Se eliminarán TODAS las postulaciones!. ¿Deseas continuar?',
      loading: false
    });
  };

  // ====== Confirmar desde modal ======
  const handleConfirm = async () => {
    if (!isAdmin) return;
    try {
      setConfirm(c => ({ ...c, loading: true }));
      if (confirm.type === 'one') {
        await performDeleteOne(confirm.payload.id);
      } else if (confirm.type === 'selected') {
        await performDeleteSelected(confirm.payload.ids);
      } else if (confirm.type === 'all') {
        await performDeleteAll();
      }
      setConfirm({ show: false, type: null, payload: null, title: '', message: '', loading: false });
    } catch (err) {
      console.error('Error al eliminar:', err);
      setConfirm(c => ({ ...c, loading: false }));
      alert('No se pudo completar la eliminación.');
    }
  };

  const handleCancel = () => {
    if (confirm.loading) return;
    setConfirm({ show: false, type: null, payload: null, title: '', message: '', loading: false });
  };

  // ====== Export ======
  const exportarExcel = () => {
    if (!filtered.length) return;

    const selectedIds = Array.from(selected);
    const rowsToExport = selectedIds.length
      ? filtered.filter(r => selected.has(r.id))   // solo seleccionados
      : filtered;                                  // si no hay selección, todos los filtrados

    const data = rowsToExport.map(r => {
      const fotoUrl = r.foto
        ? (r.foto.startsWith('/')
            ? `https://api.sevicol.com.uy${r.foto}`
            : `https://api.sevicol.com.uy/uploads/postulaciones/${r.foto}`)
        : '';

      const cvUrl = r.cv_file
        ? (r.cv_file.startsWith('/') ? `https://api.sevicol.com.uy${r.cv_file}` : r.cv_file)
        : '';
        
      return {
        Nombre: r.nombre,
        Apellido: r.apellido,
        Documento: r.documento || '',
        'Fecha Nac.': r.fecha_nac ? new Date(r.fecha_nac).toLocaleDateString() : '',
        Dirección: r.direccion || '',
        Departamento: r.departamento || '',
        Ciudad: r.ciudad || '',
        Teléfono: r.telefono || '',
        Email: r.email || '',
        Cargo: r.cargo || '',
        'Situación laboral': r.situacion_laboral || '',
        'Detalle situación': r.detalle_situacion || '',
        Estudios: r.estudios || '',
        'Ref. laborales': r.ref_laborales || '',
        'Ref. personales': r.ref_personales || '',
        Motivación: r.motivacion || '',
        Fecha: r.fecha ? new Date(r.fecha).toLocaleString() : '',
        CV: cvUrl,
        Foto: fotoUrl
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Postulaciones');
    XLSX.writeFile(wb, 'postulaciones_sevicol.xlsx');
  };


  const handleFiltro = (e) => {
    const { name, value } = e.target;
    setFiltros(f => ({ ...f, [name]: value, ...(name === 'departamento' ? { ciudad: '' } : null) }));
  };

  // helper para acortar texto de estudios
  const resumenTexto = (txt, max = 80) => {
    if (!txt) return '';
    return txt.length > max ? txt.slice(0, max) + '…' : txt;
  };

  // colSpan dinámico según si hay columnas de admin
  const colSpanTotal = isAdmin ? 18 : 17; // +1 por Documento

  return (
    <Container className="mt-5">
      <h2 className="mb-4">Postulaciones recibidas</h2>

      {/* TOOLBAR */}
      <div className="ap-toolbar">
        <div className="ap-toolbar__left ap-filters-row">
          <Form.Select
            size="sm"
            name="departamento"
            value={filtros.departamento}
            onChange={handleFiltro}
            className="ap-w-160"
          >
            <option value="">Depto</option>
            {departamentos.map(dep => <option key={dep} value={dep}>{dep}</option>)}
          </Form.Select>

          <Form.Select
            size="sm"
            name="ciudad"
            value={filtros.ciudad}
            onChange={handleFiltro}
            disabled={!filtros.departamento}
            className="ap-w-160"
          >
            <option value="">Ciudad</option>
            {ciudades.map(c => <option key={c} value={c}>{c}</option>)}
          </Form.Select>

          <Form.Select
            size="sm"
            name="cargo"
            value={filtros.cargo}
            onChange={handleFiltro}
            className="ap-w-160"
          >
            <option value="">Cargo</option>
            <option value="Administrativo">Administrativo</option>
            <option value="Limpieza">Limpieza</option>
            <option value="Técnico">Técnico</option>
            <option value="Guardia Físico">Guardia Físico</option>
          </Form.Select>

          <Form.Control
            size="sm"
            type="date"
            name="fechaDesde"
            value={filtros.fechaDesde}
            onChange={handleFiltro}
            className="ap-w-140"
            placeholder="Desde"
            title="Desde"
          />
          <Form.Control
            size="sm"
            type="date"
            name="fechaHasta"
            value={filtros.fechaHasta}
            onChange={handleFiltro}
            className="ap-w-140"
            placeholder="Hasta"
            title="Hasta"
          />

          <Form.Select
            size="sm"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="ap-w-130"
            title="Filas por página"
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}/pág</option>)}
          </Form.Select>

          <Form.Control
            size="sm"
            type="search"
            placeholder="Buscar…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="ap-flex-1"
          />
        </div>

        <div className="ap-toolbar__right">
          <Button size="sm" variant="success" onClick={exportarExcel} disabled={!filtered.length}>
            Exportar a Excel
          </Button>

          {isAdmin && (
            <Button
              size="sm"
              variant="danger"
              onClick={askDeleteSelected}
              disabled={!pageSlice.some(r => selected.has(r.id))}
            >
              Eliminar seleccionados
            </Button>
          )}
        </div>
      </div>

      {/* Tabla con wrapper scrolleable */}
      <div className="ap-table-wrapper">
        <Table striped bordered hover size="sm" className="ap-table">
          <thead>
            <tr>
              <th style={{ width: 42 }}>
                <Form.Check
                  type="checkbox"
                  checked={selectAllPage && pageSlice.length > 0}
                  onChange={toggleSelectAllPage}
                  title="Seleccionar todo (esta página)"
                />
              </th>
              <th>Foto</th>
              <th>CV</th>
              <th>Nombre</th>
              <th>Apellido</th>
              <th>Documento</th>
              <th>Fecha nac.</th>
              <th>Dirección</th>
              <th>Depto</th>
              <th>Ciudad</th>
              <th>Teléfono</th>
              <th>Email</th>
              <th>Cargo</th>
              <th>Situación</th>
              <th>Detalle</th>
              <th>Estudios</th>
              <th>Fecha</th>
              {isAdmin && <th style={{ width: 70 }}>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {pageSlice.map(r => {
              const fotoUrl = r.foto
                ? (r.foto.startsWith('/')
                    ? `https://api.sevicol.com.uy${r.foto}`
                    : `https://api.sevicol.com.uy/uploads/postulaciones/${r.foto}`)
                : null;

              const estudios = r.estudios || '';

              return (
                <tr key={r.id}>
                  <td className="text-center">
                    <Form.Check
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleRow(r.id)}
                    />
                  </td>
                  <td style={{ width: 92 }}>
                    {fotoUrl ? (
                      <a href={fotoUrl} target="_blank" rel="noopener noreferrer" title="Ver foto">
                        <img
                          src={fotoUrl}
                          alt={`${r.nombre} ${r.apellido}`}
                          style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }}
                        />
                      </a>
                    ) : '—'}
                  </td>
                  <td>
                    {r.cv_file ? (
                      <a
                        href={r.cv_file.startsWith('/') ? `https://api.sevicol.com.uy${r.cv_file}` : r.cv_file}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver CV
                      </a>
                    ) : '—'}
                  </td>
                  <td>{r.nombre}</td>
                  <td>{r.apellido}</td>
                  <td>{r.documento || '—'}</td>
                  <td>{r.fecha_nac ? new Date(r.fecha_nac).toLocaleDateString() : '—'}</td>
                  <td className="text-wrap">{r.direccion || '—'}</td>
                  <td>{r.departamento || '—'}</td>
                  <td>{r.ciudad || '—'}</td>
                  <td>{r.telefono || '—'}</td>
                  <td className="text-wrap">{r.email}</td>
                  <td>{r.cargo || '—'}</td>
                  <td>{r.situacion_laboral || '—'}</td>
                  <td className="text-wrap">{r.detalle_situacion || '—'}</td>

                  {/* ESTUDIOS RESUMIDOS + MODAL */}
                  <td className="text-wrap">
                    {estudios ? (
                      <>
                        <span>{resumenTexto(estudios, 80)}</span>
                        {estudios.length > 80 && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 ms-1 ap-link-button"
                            onClick={() =>
                              setDetalle({
                                show: true,
                                titulo: `Estudios de ${r.nombre} ${r.apellido}`,
                                texto: estudios
                              })
                            }
                          >
                            Ver más
                          </Button>
                        )}
                      </>
                    ) : '—'}
                  </td>

                  <td>{r.fecha ? new Date(r.fecha).toLocaleString() : '—'}</td>

                  {isAdmin && (
                    <td className="text-center">
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => askDeleteOne(r.id)}
                        title="Eliminar"
                      >
                        🗑️
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}

            {!pageSlice.length && (
              <tr>
                <td colSpan={colSpanTotal} className="text-center text-muted py-4">
                  No hay resultados.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Paginación */}
      <div className="d-flex justify-content-between align-items-center mt-2">
        <div className="text-muted">Página {page} de {totalPages}</div>
        <div className="d-flex gap-2">
          <Button variant="light" onClick={() => goTo(1)} disabled={page === 1}>⏮</Button>
          <Button variant="light" onClick={() => goTo(page - 1)} disabled={page === 1}>Anterior</Button>
          <Button variant="light" onClick={() => goTo(page + 1)} disabled={page === totalPages}>Siguiente</Button>
          <Button variant="light" onClick={() => goTo(totalPages)} disabled={page === totalPages}>⏭</Button>
        </div>
      </div>

      {/* ===== Modal de confirmación ===== */}
      <Modal show={confirm.show} onHide={handleCancel} centered>
        <Modal.Header closeButton={!confirm.loading}>
          <Modal.Title>{confirm.title || 'Confirmar acción'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">{confirm.message || '¿Confirmás la acción?'}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancel} disabled={confirm.loading}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirm} disabled={confirm.loading || !isAdmin}>
            {confirm.loading ? 'Eliminando…' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===== Modal detalle (Estudios) ===== */}
      <Modal
        show={detalle.show}
        onHide={() => setDetalle({ show: false, titulo: '', texto: '' })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{detalle.titulo || 'Detalle de estudios'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ whiteSpace: 'pre-wrap' }}>{detalle.texto}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDetalle({ show: false, titulo: '', texto: '' })}
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminPanel;
